/**
 * MainChart — multi-line area chart powered by Recharts (admin parity).
 *
 * Three series rendered simultaneously:
 *   • Клики        → left axis, accent-bright (sapphire)
 *   • Конверсии    → right axis, success (green)
 *   • Комиссии $   → right axis (dollar scale), warning (amber)
 *
 * Hover crosshair + tooltip shows all three values for the day.
 * Click a legend item to toggle that series.
 */
import { useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChart3 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
} from '@/components/ui';
import { formatCents, formatCentsCompact, formatNumber, formatNumberCompact } from '@/lib/format';
import type { SeriesPoint } from '@/lib/types';

type SeriesKey = 'clicks' | 'conversions' | 'commissionDollars';

const SERIES: Array<{
  key: SeriesKey;
  label: string;
  color: string;
  axis: 'L' | 'R';
  fmtFull: (v: number) => string;
}> = [
  { key: 'clicks', label: 'Клики', color: 'var(--accent-bright)', axis: 'L', fmtFull: formatNumber },
  { key: 'conversions', label: 'Конверсии', color: 'var(--success)', axis: 'L', fmtFull: formatNumber },
  { key: 'commissionDollars', label: 'Комиссии $', color: 'var(--warning)', axis: 'R', fmtFull: (v) => formatCents(Math.round(v * 100)) },
];

function shortDate(iso: string): string {
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short' }).format(new Date(iso));
}

interface TooltipPayloadEntry {
  dataKey?: string;
  name?: string;
  value?: number;
  color?: string;
}

function ChartTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const dateLabel = label ? new Date(label).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  return (
    <div
      className="rounded-[var(--radius-sm)] border border-[var(--border-strong)] shadow-[var(--shadow-lg)] overflow-hidden min-w-[180px]"
      style={{
        backgroundColor: 'color-mix(in oklch, var(--bg-base) 94%, transparent)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <div className="px-3 pt-2 pb-1.5 text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)] tabular-nums border-b border-[var(--border-subtle)]">
        {dateLabel}
      </div>
      <div className="px-3 py-2 space-y-1.5">
        {SERIES.map((s) => {
          const entry = payload.find((p) => p.dataKey === s.key);
          if (!entry || entry.value == null) return null;
          return (
            <div key={s.key} className="flex items-center justify-between gap-3 text-[11px] tabular-nums">
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: s.color, boxShadow: `0 0 4px ${s.color}` }}
                />
                <span className="text-[var(--text-muted)]">{s.label}</span>
              </span>
              <span className="text-[var(--text)] font-semibold">
                {s.fmtFull(Number(entry.value))}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChartLegend({
  hidden,
  onToggle,
}: {
  hidden: Set<SeriesKey>;
  onToggle: (k: SeriesKey) => void;
}) {
  return (
    <div className="flex items-center gap-4 flex-wrap text-[12px]">
      {SERIES.map((s) => {
        const off = hidden.has(s.key);
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onToggle(s.key)}
            className={
              'inline-flex items-center gap-1.5 cursor-pointer transition-opacity duration-150 ' +
              (off ? 'opacity-40 hover:opacity-70' : 'opacity-100 hover:opacity-80')
            }
            aria-pressed={!off}
          >
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{
                backgroundColor: s.color,
                boxShadow: off ? 'none' : `0 0 6px ${s.color}`,
              }}
            />
            <span className="text-[var(--text-secondary)]">{s.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function MainChart({ points }: { points: SeriesPoint[] }) {
  const [hidden, setHidden] = useState<Set<SeriesKey>>(new Set());

  if (points.length < 2) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <BarChart3 size={14} className="text-[var(--text-muted)]" />
              Динамика
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            icon={<BarChart3 className="h-7 w-7" />}
            title="Данных пока мало"
            description="График построится, когда наберётся хотя бы 2 точки за выбранный период."
          />
        </CardContent>
      </Card>
    );
  }

  // Recharts expects flat numeric data. commissionDollars in $ (not cents) for the right axis.
  const data = points.map((p) => ({
    date: p.date,
    clicks: p.clicks,
    conversions: p.conversions,
    commissionDollars: p.commissionCents / 100,
  }));

  const toggle = (k: SeriesKey) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            <BarChart3 size={14} className="text-[var(--text-muted)]" />
            Динамика
          </span>
        </CardTitle>
        <ChartLegend hidden={hidden} onToggle={toggle} />
      </CardHeader>
      <CardContent className="!pt-2">
        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 12, right: 16, left: 0, bottom: 4 }}>
              <defs>
                <linearGradient id="mc-clicks-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--accent-bright)" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="var(--accent-bright)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="mc-conv-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--success)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="var(--success)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="mc-commission-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--warning)" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="var(--warning)" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="3 4" vertical={false} />

              <XAxis
                dataKey="date"
                stroke="var(--text-quiet)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={shortDate}
                interval={Math.max(0, Math.floor(data.length / 7))}
                dy={4}
              />

              {/* Left axis: counts (clicks & conversions) */}
              <YAxis
                yAxisId="L"
                stroke="var(--text-quiet)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                width={46}
                tickFormatter={(v) => formatNumberCompact(Number(v))}
              />

              {/* Right axis: dollars (commission) */}
              <YAxis
                yAxisId="R"
                orientation="right"
                stroke="var(--text-quiet)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                width={48}
                tickFormatter={(v) => formatCentsCompact(Math.round(Number(v) * 100))}
              />

              <Tooltip
                cursor={{ stroke: 'var(--border-strong)', strokeWidth: 1, strokeDasharray: '3 4' }}
                content={<ChartTooltip />}
              />

              {!hidden.has('clicks') && (
                <Area
                  yAxisId="L"
                  type="monotone"
                  dataKey="clicks"
                  name="Клики"
                  stroke="var(--accent-bright)"
                  strokeWidth={2}
                  fill="url(#mc-clicks-fill)"
                  isAnimationActive
                  animationDuration={650}
                  activeDot={{ r: 4, strokeWidth: 0, fill: 'var(--accent-bright)' }}
                />
              )}
              {!hidden.has('conversions') && (
                <Area
                  yAxisId="L"
                  type="monotone"
                  dataKey="conversions"
                  name="Конверсии"
                  stroke="var(--success)"
                  strokeWidth={2}
                  fill="url(#mc-conv-fill)"
                  isAnimationActive
                  animationDuration={650}
                  animationBegin={120}
                  activeDot={{ r: 4, strokeWidth: 0, fill: 'var(--success)' }}
                />
              )}
              {!hidden.has('commissionDollars') && (
                <Area
                  yAxisId="R"
                  type="monotone"
                  dataKey="commissionDollars"
                  name="Комиссии $"
                  stroke="var(--warning)"
                  strokeWidth={2}
                  fill="url(#mc-commission-fill)"
                  isAnimationActive
                  animationDuration={650}
                  animationBegin={240}
                  activeDot={{ r: 4, strokeWidth: 0, fill: 'var(--warning)' }}
                />
              )}

              <Legend content={() => null} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
